// The encounter's step bar (Antonin, 15 Oct: "encounter, the continue button should be one container under, with a
// stepper like in the prototype. No button near tabs in header."). One container directly under the page header, on the
// Encounter tab only: the stepper on the left (the encounter's real steps, the prototype's ProgressIndex look from the
// bridge's `/* encounter step bar */` block), the page's primary action on the right (Start / Continue before the visit
// starts, Next step once it has), with its secondary actions beside it. The page header keeps identity and tabs only.
//
// The stepper is the DS `EncounterStepNav`, the same molecule and the same handler the started encounter has always
// drawn; before Start its steps are read only (there is nowhere to go until the encounter starts), so no step is a dead
// click. Look in `src/styles/showcase-bridge.css` (`/* encounter step bar */`).
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { EncounterStepNav, ProgressBar, type EncounterStepNavStep } from '@scripsteam/scrips-signal-ds';

export interface EncounterStepBarProps {
  /** The encounter's steps, in order (production's own resolved steps; frame v2 its S · O · A&P sections). */
  steps: EncounterStepNavStep[];
  /** The step in view. Absent → no step is current (the encounter has not started). */
  activeId?: string;
  /** Moves to a step (the page's own step handler). Absent → the steps are read only. */
  onStepChange?: (id: string) => void;
  /** The primary action, last, with its secondary actions and any reason before it. Absent → no action column. */
  actions?: ReactNode;
  /** The share of steps done (0 to 100), drawn as the line under the row. Absent → the hairline alone. */
  progress?: number;
  /** the left panel's toggle, centred on this bar's bottom edge (the seam to the panels below). Absent → as before. */
  seamToggle?: ReactNode;
  /** slide the bar in from the top once; `onRevealed` fires when the animation ends. */
  reveal?: boolean;
  onRevealed?: () => void;
  /** Steps that need attention → what is missing (up to three). Drawn app-side: the DS step nav has no such state (DS item). */
  attention?: Record<string, string[]>;
}

export function EncounterStepBar({ steps, activeId, onStepChange, actions, progress, seamToggle, reveal, onRevealed, attention }: EncounterStepBarProps) {
  const readOnly = !onStepChange;
  const barRef = useRef<HTMLDivElement>(null);
  // The DS cell is the DS's own element, so the marker is set on it by its `data-step` after each render: `data-attention`
  // (the bridge draws the warning circle) and the missing items as its `title`.
  useLayoutEffect(() => {
    const cells = barRef.current?.querySelectorAll<HTMLElement>('nav[aria-label="Encounter steps"] > button[data-step]') ?? [];
    cells.forEach((cell) => {
      const missing = attention?.[cell.dataset.step ?? ''];
      if (missing && missing.length > 0) {
        cell.setAttribute('data-attention', 'true');
        cell.setAttribute('title', `Still needed: ${missing.join(', ')}`);
      } else {
        cell.removeAttribute('data-attention');
        cell.removeAttribute('title');
      }
    });
  });
  return (
    <div
      ref={barRef}
      data-slot="encounter-step-bar"
      data-read-only={readOnly ? 'true' : undefined}
      data-seam-toggle={seamToggle ? 'true' : undefined}
      data-reveal={reveal ? 'true' : undefined}
      onAnimationEnd={reveal ? onRevealed : undefined}
    >
      <div data-slot="encounter-step-bar-row">
        <div data-slot="encounter-step-bar-steps">
                    <EncounterStepNav
            steps={readOnly ? steps.map((s) => ({ ...s, disabled: true })) : steps}
            activeId={activeId}
            onStepChange={onStepChange}
          />
        </div>
        {actions ? <div data-slot="encounter-step-bar-actions">{actions}</div> : null}
      </div>
      {seamToggle}
      {progress != null ? <ProgressBar value={progress} aria-label="Encounter progress" /> : null}
    </div>
  );
}

